import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { profileImageSrc } from "../lib/profileImage";
import { isPasswordValid, PASSWORD_MIN_LEN } from "../lib/passwordPolicy";
import PasswordStrengthChecklist from "./PasswordStrengthChecklist";

export function ProfileEditModal({ onClose, onSaved }) {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    job_title: user?.job_title || "",
  });
  const [photoUrl, setPhotoUrl] = useState(user?.profile_image_url || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const initials = ((user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")).toUpperCase() || "?";

  const onPickPhoto = () => fileInputRef.current?.click();

  const onPhotoChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Only image files are accepted."); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Image too large — max 2MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload/profile-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(data.profile_image_url);
      if (refresh) await refresh();
      toast.success("Photo updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    }
    setUploading(false);
  };

  const removePhoto = async () => {
    if (!photoUrl) return;
    setUploading(true);
    try {
      await api.delete("/upload/profile-photo");
      setPhotoUrl(null);
      if (refresh) await refresh();
      toast.success("Photo removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/auth/me/profile", form);
      if (refresh) await refresh();
      toast.success("Profile updated");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
    setSaving(false);
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-testid="profile-edit-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">Edit Profile</h2>
          <div className="mono text-[11px] text-mute mt-1">Update your name &amp; email</div>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block mono text-[11px] text-mute mb-2 uppercase tracking-wider">Profile photo</label>
            <div className="flex items-center gap-4 flex-wrap">
              <div
                data-testid="profile-photo-preview"
                style={{
                  width: 72, height: 72, borderRadius: "50%",
                  background: "var(--pg-surface-2)",
                  border: "1px solid var(--pg-border)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  overflow: "hidden",
                  fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
                  fontSize: 20, color: "var(--pg-text-mute)",
                  letterSpacing: "0.04em",
                }}
              >
                {photoUrl
                  ? <img src={profileImageSrc(photoUrl)} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : initials}
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <button type="button" className="pg-btn" onClick={onPickPhoto} disabled={uploading} data-testid="profile-photo-upload-btn">
                    {uploading ? "Uploading…" : (photoUrl ? "Change" : "Upload photo")}
                  </button>
                  {photoUrl && (
                    <button type="button" className="pg-btn" onClick={removePhoto} disabled={uploading} data-testid="profile-photo-remove-btn">
                      Remove
                    </button>
                  )}
                </div>
                <div className="mono text-[10px] text-mute">JPG or PNG. Max 2MB. Auto-cropped to a square.</div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onPhotoChange}
                style={{ display: "none" }}
                data-testid="profile-photo-input"
              />
            </div>
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">First name</label>
            <input className="pg-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="profile-input-first-name" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Last name</label>
            <input className="pg-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="profile-input-last-name" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Email</label>
            <input className="pg-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="profile-input-email" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Job Title</label>
            <input
              className="pg-input"
              value={form.job_title}
              onChange={(e) => setForm({ ...form, job_title: e.target.value })}
              placeholder="e.g. Account Executive, SDR, Head of Sales"
              data-testid="profile-input-job-title"
            />
            <div className="mono text-[10px] text-mute mt-1">Shown on your profile — separate from your permission role.</div>
          </div>
          <div style={{
            background: "var(--pg-surface-soft, rgba(0,0,0,0.03))",
            border: "1px solid var(--pg-border)",
            borderRadius: 8,
            padding: "12px 14px",
            fontSize: 12,
            color: "var(--pg-text-mute)",
          }}>
            <strong style={{ color: "var(--pg-text)" }}>Email signature</strong> is managed in{" "}
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/settings?tab=integrations#gmail-settings");
              }}
              data-testid="profile-email-signature-link"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "var(--pg-accent, #5B4FE8)",
                textDecoration: "underline",
                cursor: "pointer",
                font: "inherit",
                fontWeight: 600,
              }}
            >
              Settings → Integrations → Gmail settings
            </button>.
          </div>
        </form>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button onClick={submit} className="pg-btn pg-btn-primary" disabled={saving} data-testid="profile-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </>
  );
}

export function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!isPasswordValid(form.new_password)) {
      toast.error(`Password must be at least ${PASSWORD_MIN_LEN} characters and include a letter, a number, and a symbol.`);
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/auth/me/password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
    setSaving(false);
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-testid="change-password-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">Change Password</h2>
          <div className="mono text-[11px] text-mute mt-1">
            Minimum {PASSWORD_MIN_LEN} characters with a letter, number, and symbol.
          </div>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Current password</label>
            <input className="pg-input" type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} required data-testid="password-input-current" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">New password</label>
            <input className="pg-input" type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} required minLength={PASSWORD_MIN_LEN} data-testid="password-input-new" />
            <PasswordStrengthChecklist password={form.new_password} testid="change-password-rules" />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Confirm new password</label>
            <input className="pg-input" type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required minLength={PASSWORD_MIN_LEN} data-testid="password-input-confirm" />
          </div>
        </form>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button onClick={submit} className="pg-btn pg-btn-primary" disabled={saving} data-testid="password-save">
            {saving ? "Saving…" : "Change Password"}
          </button>
        </div>
      </div>
    </>
  );
}
